// src/api/utils/captureError.ts

/**
 * Centralized Error Capture Utility
 *
 * Single entry point for streaming errors to PostHog with consistent metadata.
 * Error type (server vs client) is automatically inferred from the API name.
 * Error code is extracted from the error object if available.
 */

import posthog from "../../utils/posthog";
import { track } from "../../utils/analytics";
import { getNetworkDiagnostics } from "../../utils/networkMonitor";
import { ErrorCodes, ApiNames, type ApiName } from "./errorCatalog";

// Re-export for convenience
export { ErrorCodes, ApiNames };
export type { ApiName };

/**
 * Extract error code from an error object if it has one.
 * Supports common error shapes: { errorCode }, { error_code }, { code }
 */
function extractErrorCode(error: unknown): string | undefined {
  if (error && typeof error === "object") {
    const err = error as Record<string, unknown>;
    if (typeof err.errorCode === "string") return err.errorCode;
    if (typeof err.error_code === "string") return err.error_code;
    if (typeof err.code === "string") return err.code;
  }
  return undefined;
}

/**
 * Extract the HTTP status code from an error object if it has one.
 * Supports the shapes used across our API error classes: { statusCode } and
 * { status_code }. Client-side failures carry statusCode 0 (no response) -
 * attached as-is, and excluded from the 4xx product-event routing below.
 */
function extractStatusCode(error: unknown): number | undefined {
  if (error && typeof error === "object") {
    const err = error as Record<string, unknown>;
    if (typeof err.statusCode === "number") return err.statusCode;
    if (typeof err.status_code === "number") return err.status_code;
  }
  return undefined;
}

/**
 * Pull a queryable name/message off the error.
 *
 * PostHog's own exception capture has historically landed $exception_message as
 * null for these streamed failures, so we attach the underlying name/message as
 * plain, reliably-queryable properties. This is what makes a future occurrence
 * self-diagnosing (e.g. "TypeError: Failed to fetch" vs a real API error).
 */
function extractErrorDetail(error: unknown): { error_name?: string; error_message?: string } {
  if (error instanceof Error) {
    return { error_name: error.name, error_message: error.message };
  }
  if (typeof error === "string") {
    return { error_message: error };
  }
  return {};
}

/**
 * Stream an error to PostHog with consistent metadata.
 *
 * Error code resolution (in order):
 * 1. Extract from error object (errorCode, error_code, or code property)
 * 2. Use provided errorCode argument
 * 3. Fall back to "unknown_error"
 *
 * Error type is automatically inferred:
 * - WORD_API → client_side_error
 * - All other APIs → server_side_error
 *
 * @example
 * // Error code extracted from error.errorCode automatically
 * streamErrorToPostHog(chatApiError, ApiNames.CHAT_API);
 *
 * // Explicit error code when error object doesn't have one
 * streamErrorToPostHog(new Error("failed"), ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
 *
 * // Attach call-site context (e.g. stream progress) via `extra`
 * streamErrorToPostHog(err, ApiNames.CHAT_API, undefined, { elapsed_ms: 8200, chunks_received: 42 });
 *
 * Every captured error is automatically annotated with the connectivity context
 * (network_online, recently_offline, ...) so a client-side network drop can be
 * distinguished from a genuine backend failure at query time.
 *
 * @param error - The error to capture
 * @param apiName - The API/service where the error occurred
 * @param errorCode - Optional fallback error code (used if error object doesn't have one)
 * @param extra - Optional call-site metadata merged into the event (e.g. elapsed_ms, chunks_received)
 */
export function streamErrorToPostHog(
  error: unknown,
  apiName: ApiName,
  errorCode?: string,
  extra?: Record<string, unknown>
): void {
  const resolvedErrorCode = extractErrorCode(error) ?? errorCode ?? ErrorCodes.UNKNOWN_ERROR;
  const errorType = apiName === ApiNames.WORD_API ? "client_side_error" : "server_side_error";
  const statusCode = extractStatusCode(error);
  const detail = extractErrorDetail(error);

  const properties = {
    error_type: errorType,
    error_code: resolvedErrorCode,
    api_name: apiName,
    status_code: statusCode,
    ...detail,
    ...getNetworkDiagnostics(),
    ...extra,
  };

  // A 4xx means the backend understood the request and rejected it on purpose
  // (validation, auth, permissions) - a user-flow outcome, not a defect. Keep
  // Error tracking signal-only by recording these as a product event instead
  // of an exception. Exceptions to the rule: 408 (timeout) and 429 (rate
  // limit) signal server pressure, not a deliberate rejection - they stay in
  // Error tracking. (statusCode 0 = client-side failure, also an exception.)
  const isDeliberateRejection =
    statusCode !== undefined &&
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408 &&
    statusCode !== 429;
  if (isDeliberateRejection) {
    track("api:request_rejected", properties);
    return;
  }

  posthog.captureException(error, {
    ...properties,
    // Group Error tracking issues by what the error IS, not by stack frame.
    // The default stack fingerprint lumps every error built in a shared
    // helper (createApiError & co.) into one issue with a stale first-seen
    // title. Never include the message here: backend messages embed dynamic
    // content (tenant ids, file names) and would shatter grouping.
    $exception_fingerprint: [detail.error_name ?? "Error", apiName, resolvedErrorCode].join(":"),
  });
}
